import { api } from '@/lib/api';
import type {
  BulkValidateRequest,
  BulkValidateResponse,
  DceAnalysis,
  DceProfile,
  DceRequirement,
  DceRequiredDocument,
  ListRequirementsFilters,
  PresignedDownloadUrl,
  RunAnalysisResponse,
} from '../types/dce-analysis.types';

const BASE = (opportunityId: number) =>
  `/tm/opportunities/${opportunityId}/analysis`;

export const dceAnalysisService = {
  async getCurrent(opportunityId: number): Promise<DceAnalysis | null> {
    const { data } = await api.get<DceAnalysis | null>(BASE(opportunityId));
    return data;
  },

  async getHistory(opportunityId: number): Promise<DceAnalysis[]> {
    const { data } = await api.get<DceAnalysis[]>(`${BASE(opportunityId)}/history`);
    return data;
  },

  async getHistoryDetail(
    opportunityId: number,
    analysisId: number,
  ): Promise<DceAnalysis> {
    const { data } = await api.get<DceAnalysis>(
      `${BASE(opportunityId)}/history/${analysisId}`,
    );
    return data;
  },

  async listRequirements(
    opportunityId: number,
    filters: ListRequirementsFilters = {},
  ): Promise<DceRequirement[]> {
    const { data } = await api.get<DceRequirement[]>(
      `${BASE(opportunityId)}/requirements`,
      { params: filters },
    );
    return data;
  },

  async listProfiles(opportunityId: number): Promise<DceProfile[]> {
    const { data } = await api.get<DceProfile[]>(`${BASE(opportunityId)}/profiles`);
    return data;
  },

  async listRequiredDocuments(
    opportunityId: number,
  ): Promise<DceRequiredDocument[]> {
    const { data } = await api.get<DceRequiredDocument[]>(
      `${BASE(opportunityId)}/required-documents`,
    );
    return data;
  },

  async runAnalysis(
    opportunityId: number,
    force = false,
  ): Promise<RunAnalysisResponse> {
    const { data } = await api.post<RunAnalysisResponse>(
      `${BASE(opportunityId)}/run`,
      { force },
    );
    return data;
  },

  async updateRequirement(
    opportunityId: number,
    requirementId: number,
    payload: Partial<{
      humanStatus: DceRequirement['humanStatus'];
      statement: string;
      humanNotes: string;
      type: DceRequirement['type'];
      mandatory: boolean;
    }>,
  ): Promise<void> {
    await api.patch(
      `${BASE(opportunityId)}/requirements/${requirementId}`,
      payload,
    );
  },

  async bulkValidateRequirements(
    opportunityId: number,
    payload: BulkValidateRequest,
  ): Promise<BulkValidateResponse> {
    const { data } = await api.post<BulkValidateResponse>(
      `${BASE(opportunityId)}/requirements/bulk-validate`,
      payload,
    );
    return data;
  },

  async updateProfile(
    opportunityId: number,
    profileId: number,
    payload: Partial<{
      humanStatus: DceProfile['humanStatus'];
      jobTitle: string;
      humanNotes: string;
    }>,
  ): Promise<void> {
    await api.patch(`${BASE(opportunityId)}/profiles/${profileId}`, payload);
  },

  async updateRequiredDocument(
    opportunityId: number,
    docId: number,
    payload: Partial<{
      humanStatus: DceRequiredDocument['humanStatus'];
      pieceType: DceRequiredDocument['pieceType'];
      mandatory: boolean;
      humanNotes: string;
    }>,
  ): Promise<void> {
    await api.patch(
      `${BASE(opportunityId)}/required-documents/${docId}`,
      payload,
    );
  },

  async forceProfilesApplicable(
    opportunityId: number,
    forceApplicable: boolean,
  ): Promise<void> {
    await api.post(`${BASE(opportunityId)}/profiles/force-applicable`, {
      forceApplicable,
    });
  },

  async getDocumentPresignedUrl(
    documentId: number,
  ): Promise<PresignedDownloadUrl> {
    const { data } = await api.post<PresignedDownloadUrl>(
      `/tm/documents/${documentId}/presigned-url`,
    );
    return data;
  },
};
